import { ChangeEvent, useCallback, useContext, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { DataType } from "../../services/CompanyService";
import { FilterCollection } from "../../services/FilterService";
import { TranslationService } from "../../services/TranslationService";
import AdvancedFilters, { AdvancedFiltersButton } from "../shared/components/AdvancedFilters";
import Dropdown from "../shared/components/Dropdown";
import Table, { TableHeaderWithFieldId } from "../shared/Table";
import TableContext from "../task/TableContext";
import InboxListContext from "./InboxListContext";
import ActivityService from "../../services/ActivityService";
import { ToastService } from "../shared/bootstrap/Toast";
import TabPositionContext from "../shared/TabPositionContext";
import { InboxTableItem, getInboxTableHeaders } from "./InboxTableItem";
import { InboxContext } from "./InboxTableProvider";
import Switch from "react-switch";
import SearchInput from "../shared/components/SearchInput";
import Collapsable from "../shared/bootstrap/Collapsable";
import TooltipComponent from "../shared/TooltipComponent";
import archiveSrc from "../../assets/img/archive_white_20dp.png";
import unarchiveSrc from "../../assets/img/move_to_inbox_white_20dp.png";
import markReadSrc from "../../assets/img/mark_read_white_20dp.png";
import { ActivityListResponse } from "../client/activity/entities/AcitivityListResponse";
import { addIf } from "../../utils/Utils";

const READ_STATES = {
    ALL: "0",
    READ: "1",
    NOT_READ: "2",
    ONLY_ARCHIVED: "3"
};

export const InboxList = ({ quickfilter, loading }: { quickfilter: number; loading: boolean }) => {
    const [includeArchived, setIncludeArchived] = useState(false);
    const [readStatus, setReadStatus] = useState<string>(READ_STATES.NOT_READ);
    const { search } = useLocation();
    const filterQS = new URLSearchParams(search).get("filter") ?? "";
    const { translate } = TranslationService;
    const { reload, applySearch } = useContext(TableContext);
    const { setRequest, request, response } = useContext(InboxContext);
    const { getCounterInbox } = useContext(TabPositionContext);
    const { messageIds, messageAll, setMessageAll, setMessageIds } = useContext(InboxListContext);
    const emailToMessage = (email: ActivityListResponse.Item) => ({ id: email.ID, read: Boolean(email.Read), archived: Boolean(email.Archived) });
    const emailIdToMessage = useCallback((id: number) => {
        const email = response?.list.find(x => x.ID === id);
        if (!email) {
            return undefined;
        }
        return emailToMessage(email);
    }, [response?.list]);
    const responseIds: number[] = useMemo(() => response?.list ? response?.list.map(x => x.ID) : [], [response?.list]);

    const messagesIdsSelected = useMemo(() => {
        const messagesSelected: number[] = [];
        messageIds.forEach(message => {
            if (responseIds.includes(message)) {
                messagesSelected.push(message);
            }
        });
        return messagesSelected;
    }, [messageIds, responseIds]);

    const typeValues = useMemo(() => {
        const values = [
            { Value: translate.WhatsAppAll, Id: READ_STATES.ALL },
            { Value: translate.WhatsAppRead, Id: READ_STATES.READ },
            { Value: translate.WhatsAppNotRead, Id: READ_STATES.NOT_READ },
        ];
        if (includeArchived) {
            values.push({ Value: translate.Archived, Id: READ_STATES.ONLY_ARCHIVED });
        }
        return values;
    }, [includeArchived, translate]);

    const onApplySearch = (keyword: string) => {
        applySearch(keyword);
    };

    const getPagedMessageIds = () =>
        Array.from(document.getElementsByClassName("checkbox-inbox")).reduce<number[]>(
            (ids, element) => {
                const inputElement = element as HTMLInputElement;
                const invoiceId = inputElement.dataset["invoiceselected"];
                if (invoiceId) ids.push(parseInt(invoiceId));
                return ids;
            },
            []
        );

    const toggleOneCheckbox = (itemId: number, checked: boolean) => {
        setMessageIds(checked ? [...messageIds, itemId] : messageIds.filter(x => x !== itemId));
        setMessageAll(false);
    };

    const tableHeaders = useMemo(() => {
        const toggleAllCheckboxes = (event: ChangeEvent<HTMLInputElement>) => {
            const checked = event.target.checked;
            setMessageIds(checked ? getPagedMessageIds() : []);
            setMessageAll(checked);
        };
        const headers = getInboxTableHeaders(quickfilter);
        headers.unshift(new TableHeaderWithFieldId("Checkbox", () => (<input type="checkbox" onChange={toggleAllCheckboxes} checked={messageAll} />
        ), false, false, "checkbox", DataType.IOStatus, "w-30px"));
        return headers;
    }, [quickfilter, setMessageIds, setMessageAll, messageAll]);

    const handleArchiveMessages = async () => {
        if (messagesIdsSelected.length) {
            const result = await ActivityService.setArchived({ ids: messagesIdsSelected, archived: "1" });
            if (result instanceof Error) {
                ToastService.showToast(TranslationService.translate.ErrorProcessingRequest, undefined, "danger");
            } else {
                await controlCheckActions();
                await getCounterInbox();
                await reload();
            }
        }
    };

    const handleUnarchiveMessages = async () => {
        if (messagesIdsSelected.length) {
            const result = await ActivityService.setArchived({ ids: messagesIdsSelected, archived: "0" });
            if (result instanceof Error) {
                ToastService.showToast(TranslationService.translate.ErrorProcessingRequest, undefined, "danger");
            } else {
                await controlCheckActions();
                await getCounterInbox();
                await reload();
            }
        }
    };

    const handleReadStatusChange = async (read: boolean) => {
        if (messagesIdsSelected.length) {
            const result = await ActivityService.setRead({ ids: messagesIdsSelected, read: read ? "1" : "0" });
            if (result instanceof Error) {
                ToastService.showToast(TranslationService.translate.ErrorProcessingRequest, undefined, "danger");
            } else {
                await controlCheckActions();
                await getCounterInbox();
                await reload();
            }
        }
    };

    const controlCheckActions = () => {
        if (messageAll) {
            setMessageAll(false);
            setMessageIds([]);
        }
    }
    const handleGetStatus = (status: string) => {
        setReadStatus(status);
        if (status === READ_STATES.ALL) {
            const { read, archived, ...restRequest } = request || {};
            // @ts-ignore
            setRequest(includeArchived ? { ...restRequest } : { ...restRequest, archived: false });
        } else if (status === READ_STATES.ONLY_ARCHIVED) {
            setRequest(prev => ({ ...prev, read: true, archived: true }));
        } else if (status === READ_STATES.NOT_READ && includeArchived) {
            const { read, archived, ...restRequest } = request || {};
            // @ts-ignore
            setRequest(({ ...restRequest, read: false }));
        } else {
            const { read, archived, ...restRequest } = request || {};
            setRequest(prev => includeArchived ?
                ({ ...prev, ...restRequest, read: status === READ_STATES.READ })
                : ({ ...prev, ...restRequest, read: status === READ_STATES.READ, archived: false }));
        }
    };

    const toggleArchived = () => {
        if (includeArchived) {
            setReadStatus(READ_STATES.NOT_READ);
            setRequest(prev => ({ ...prev, archived: false, read: false }));
        } else {
            const { read, archived, ...restRequest } = request || {};
            setReadStatus(READ_STATES.ALL);
            // @ts-ignore
            setRequest(restRequest);
        }
        setIncludeArchived(!includeArchived);
    };

    const messageIdWithInfo = messageIds.map(emailIdToMessage).filterFalsey();
    const buttonConfigs = [
        ...addIf(messageAll || messageIdWithInfo.some(x => !x.archived), {
            title: translate.Archive,
            onClick: handleArchiveMessages,
            iconClass: archiveIcon,
        }),
        ...addIf(messageAll || messageIdWithInfo.some(x => x.archived), {
            title: translate.Unarchive,
            onClick: handleUnarchiveMessages,
            iconClass: unarchiveIcon,
        }),
        ...addIf(messageAll || messageIdWithInfo.some(x => !x.read), {
            title: translate.MarkAsRead,
            onClick: () => handleReadStatusChange(true),
            iconClass: markReadIcon,
        }),
        ...addIf(messageAll || messageIdWithInfo.some(x => x.read), {
            title: translate.MarkAsNotRead,
            onClick: () => handleReadStatusChange(false),
            iconClass: markUnreadIcon,
        }),
    ];

    return (
        <div className="container-fluid padding">
            <div className="mh-100 p-0">
                <div className="genericHeader justify-content-between">
                    <div className="d-flex col-md-6 align-items-center">
                        <div className="col-auto me-2">
                            <AdvancedFiltersButton />
                        </div>
                        <div className="dropdown-wrapper me-4">
                            <Dropdown
                                quickFilter
                                defaultValue={readStatus}
                                items={typeValues}
                                onChange={handleGetStatus}
                                value={readStatus}
                            />
                        </div>
                        <div className="d-flex col-md-4 gap-2 ms-4">
                            <label className="form-label align-items-center" style={{ display: "contents" }}>{translate.FilterArchive}</label>
                            <Switch
                                uncheckedIcon={false}
                                checkedIcon={false}
                                checked={includeArchived}
                                onChange={toggleArchived}
                                height={24}
                                width={50}
                            />
                        </div>
                    </div>
                    <SearchInput onSearch={onApplySearch} autosearch={false} />
                </div>
                <div className="pt-0">
                    <AdvancedFilters defaultValue={filterQS} page={FilterCollection.ReportActivity} />
                </div>

                <div className="px-4 pb-3">
                    <Collapsable show={messagesIdsSelected.length > 0 || messageAll}>
                        <div className="selectedCollapse mt-3" style={{ width: "fit-content" }}>
                            {buttonConfigs.map((config, index) => (
                                <TooltipComponent title={config.title} key={config.title}>
                                    <button type="button" className={"btn text-secondary"} onClick={config.onClick}>
                                        {config.iconClass}
                                    </button>
                                </TooltipComponent>
                            ))}
                        </div>
                    </Collapsable>
                </div>
                {loading ? (
                    <div className={`table-loader-loading-fade ${loading ? " background-fade min-vh-75" : ""}`}>
                        <i className="fas fa-spinner fa-pulse fa-8x"></i>
                    </div>
                ) : (
                    <Table
                        headers={tableHeaders}
                        item={InboxTableItem(toggleOneCheckbox, tableHeaders)}
                        stickyHeader
                        additionalClass={'mh-50p'}
                    />
                )}
            </div>
        </div>

    );
};

const markUnreadIcon = <svg viewBox="0 -960 960 960" height="20" width="20" focusable="false" className="inboxIcon"><path d="M168-192q-29.7,0-50.85-21.16T96-264.04V-696.28Q96-726 117.15-747T168-768H553q-2,17-1,35.5t6,36.5H168L480-517l140-81q14,13 37,24t41,16L480-432L168-611v347H792V-558.46q20-4.54 37.5-14.04T864-594v329.77Q864-234 842.5-213T792-192H168Zm0-504v432V-696Zm576,72q-50,0-85-35t-35-85t35-85t85-35t85,35t35,85t-35,85t-85,35Z" fill="#0076b6"></path></svg>;
const archiveIcon = <div className="inboxIcon maskIcon" style={{ WebkitMaskBoxImage: "url(" + archiveSrc + ")" }}></div>;
const unarchiveIcon = <div className="inboxIcon maskIcon" style={{ WebkitMaskBoxImage: "url(" + unarchiveSrc + ")" }}></div>;
const markReadIcon = <div className="inboxIcon maskIcon" style={{ WebkitMaskBoxImage: "url(" + markReadSrc + ")" }}></div>;
